// The Bloom connector: outside AIs (Claude connectors, Claude Code, any MCP client) and scripts reaching Bloom apps as
// a member, with the member's permission. Types shared by the Worker (worker/src/connector/) and the web app (the
// consent screen and Settings → Connected apps & AI). See docs/connector.md.

/** Read: list apps, query their data, read History. Act: also run the apps' commands (as the member). */
export type ConnectorAccess = "read" | "act";
/** Channel messages: none, read them, or read and send (as the member, marked with the AI's name). */
export type MessageAccess = "off" | "read" | "send";
/** DMs: none, or read. AIs never send in DMs. */
export type DmAccess = "off" | "read";

/** What a grant allows in one server. */
export interface GrantServerScope {
  serverId: string;
  /** "all": every app, now and future. Otherwise these apps' slugs. */
  apps: "all" | string[];
  access: ConnectorAccess;
  /**
   * Build apps (docs/blooming.md "Building from your own AI"): read blooming threads' code, run Bloom's checks, add
   * drafts and start threads, as the member, by the same rules as prompting Bloom in the thread. Publishing still
   * needs the member to be the app's owner or editor (or a server owner or admin).
   */
  build?: boolean;
  /** Channel messages (and their threads). Default off. */
  messages?: MessageAccess;
  /** Which text channels messages covers: "all" (now and future) or these channel ids. Default all. */
  channels?: "all" | string[];
  /** The member's DMs (and threads under them). Default off. */
  dms?: DmAccess;
  /** Which DMs: "all", or only DMs whose other people are all among these user ids. Default all. */
  dmPeople?: "all" | string[];
}

/** A grant as Settings lists it: one per AI client (OAuth) or per personal access token. */
export interface ConnectorGrant {
  id: string;
  kind: "oauth" | "pat";
  /** The client's name (as it registered itself) or the token's name. */
  name: string;
  /** OAuth: where the client sends people back to (the host the consent screen showed). */
  host?: string;
  createdAt: number;
  updatedAt: number;
  lastUsedAt: number | null;
  /** Personal access tokens only. */
  expiresAt: number | null;
  servers: (GrantServerScope & { serverName: string })[];
}

/** One server on the consent screen (and the grant editor): the member's role and the server's apps. */
export interface ConnectorServerChoice {
  id: string;
  name: string;
  color: string;
  role: string;
  apps: { slug: string; name: string; icon: string; description: string }[];
  /** Text channels, for choosing which ones messages covers. */
  channels?: { id: string; name: string }[];
  /** The other members (people, not bots), for choosing whose DMs. */
  people?: { id: string; name: string }[];
}

/** GET /api/connector/authorize: what the consent screen shows. */
export interface AuthorizePreview {
  client: {
    id: string;
    name: string;
    /** The redirect URI's host (the spec asks us to show it). */
    host: string;
    /** Sends you back to this computer (localhost): any program on it could be the one asking. */
    localhost: boolean;
    /** "cimd": the client's name came from a document at its own https address; "dcr": it registered itself. */
    kind: "dcr" | "cimd";
  };
  servers: ConnectorServerChoice[];
  /** The member's current grant for this client, if they connected it before (to start from). */
  current: GrantServerScope[] | null;
  /** The client asked to act (scope bloom:act), not just read. */
  wantsAct: boolean;
}

/** POST /api/connector/tokens: a personal access token, shown once. */
export interface CreatedToken {
  token: string;
  grant: ConnectorGrant;
}

/** Lifetimes a personal access token can have, in days. */
export const PAT_DAYS = [7, 30, 90, 365] as const;
/** Longest names for tokens and clients (they show in History as "<name> on behalf of <member>"). */
export const CONNECTOR_NAME_MAX = 32;
/** The OAuth scopes (coarse: the real permissions are the grant's per-server choices). */
export const SCOPE_READ = "bloom:read";
export const SCOPE_ACT = "bloom:act";

/** GET /api/connector/dm-readers: an AI that can read a DM, and whose it is (for the line at the top of the DM). */
export interface DmReader {
  userId: string;
  /** The connection's name ("Claude Code"). */
  client: string;
}

/** A conversation as the reach rule sees it: a text channel (or a thread in one), or a DM (or a thread under one). */
export type ReachStream = { kind: "text"; channelId: string } | { kind: "dm"; members: string[]; formerly?: string[] };

/**
 * What a grant may do with one conversation, for its member `userId`: the one rule the connector asks for every
 * conversation it lists, reads or posts in (docs/connector.md "Messages"). Channel messages follow the grant's
 * messages choice and channel list. DMs are read at most, never sent in, and only when every other person in the DM
 * is among the chosen people and none of them has said no to the AIs of people they DM with (`refuses`). "Every other
 * person" includes anyone who wrote there and has since left (`formerly`): leaving doesn't take back their no.
 */
export function messageReach(scope: Pick<GrantServerScope, "messages" | "channels" | "dms" | "dmPeople">, userId: string, stream: ReachStream, refuses: (userId: string) => boolean): MessageAccess {
  if (stream.kind === "text") {
    const access = scope.messages ?? "off";
    if (access === "off") return "off";
    const channels = scope.channels ?? "all";
    return channels === "all" || channels.includes(stream.channelId) ? access : "off";
  }
  if ((scope.dms ?? "off") === "off" || !stream.members.includes(userId)) return "off";
  const others = stream.members.filter((m) => m !== userId);
  // Nobody else left in it (they left the server): it isn't read.
  if (!others.length) return "off";
  const everyone = [...new Set([...others, ...(stream.formerly ?? []).filter((m) => m !== userId)])];
  if (everyone.some(refuses)) return "off";
  const people = scope.dmPeople ?? "all";
  return people === "all" || everyone.every((o) => people.includes(o)) ? "read" : "off";
}
